import fs from 'fs'
import path from 'path'

import type { DiscoveredService } from './discover-services'

export interface PublicFile {
  serviceId: string
  /** Path relative to public/, e.g. "icon.webp" */
  relativePath: string
  buffer: Buffer
}

export interface PublicFilesResult {
  files: PublicFile[]
  /** Icons a service declares that are not on disk — the deploy goes ahead without them. */
  missing: { serviceId: string; relativePath: string }[]
}

/**
 * Collects all public files referenced by the given service definitions (e.g. integrationIcon).
 * Icon paths in JSDoc use a leading slash relative to public/ (e.g. "/icon.webp").
 */
export function collectPublicFiles(
  services: DiscoveredService[],
  projectRoot: string = process.cwd()
): PublicFilesResult {
  const files: PublicFile[] = []
  const missing: { serviceId: string; relativePath: string }[] = []

  for (const service of services) {
    // Per service, not per project: icons live in `services/<id>/public/`. Guarding on a root `public/`
    // directory instead used to drop every icon in a project that has no such folder.
    const servicePublicDir = path.resolve(projectRoot, 'services', service.serviceId, 'public')

    if (!fs.existsSync(servicePublicDir)) {
      continue
    }

    const iconPath = service.serviceInfo.integrationIcon

    if (!iconPath) {
      continue
    }

    // Strip leading slash — "/icon.webp" → "icon.webp"
    const relativePath = iconPath.replace(/^\//, '')
    const fullPath = path.resolve(servicePublicDir, relativePath)

    // Confine the resolved path to public/. The separator is what makes this a containment check rather than
    // a prefix match: without it, `services/<id>/public-anything/` also starts with the directory name.
    if (!fullPath.startsWith(servicePublicDir + path.sep)) {
      continue
    }

    if (!fs.existsSync(fullPath)) {
      missing.push({ serviceId: service.serviceId, relativePath })

      continue
    }

    files.push({
      relativePath,
      serviceId: service.serviceId,
      buffer   : fs.readFileSync(fullPath),
    })
  }

  return { files, missing }
}
